import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import ConsuTabla from '../components/ConsuTabla';
import ConsuCards from '../components/ConsuCards';
import '../css/HorariosConsulta.css';
import { Link } from 'react-router-dom';

const RAW_API_BASE_URL = process.env.REACT_APP_HCD_API_BASE_URL || '/hcd/';
const API_BASE_URL = RAW_API_BASE_URL.endsWith('/')
  ? RAW_API_BASE_URL
  : `${RAW_API_BASE_URL}/`;

const SEDES = [
  { value: '1', label: 'Sede Mendoza' },
  { value: '2', label: 'Sede San Rafael' },
  { value: '3', label: 'Sede Gral. Alvear' },
  { value: '4', label: 'Sede Este' },
];

const CARRERAS_CONFIG = [
  { value: '003', label: 'Lic. en Administración', habilitada: true, sedes: ['1'] },
  { value: '004', label: 'Lic. en Economía', habilitada: true, sedes: ['1'] },
  { value: '006', label: 'LNRG', habilitada: false, sedes: ['3', '4'] },
  { value: '007', label: 'Lic. en Logística', habilitada: true, sedes: ['1', '4'] },
  { value: '008', label: 'Contador Público', habilitada: true, sedes: ['1', '2'] },
];

const CARRERAS = CARRERAS_CONFIG.filter((c) => c.habilitada);

const PLANES_DEFAULT = [
  { value: '4', label: 'Plan 19' },
  { value: '5', label: 'Plan 26' },
];

const PLANES_LOGISTICA = [
  // En la base histórica de Logística: pl=1 corresponde al Plan 16 y pl=2 al Plan 25.
  { value: '1', label: 'Plan 16' },
  { value: '2', label: 'Plan 25' },
];

const SEDES_POR_CARRERA = Object.fromEntries(
  CARRERAS_CONFIG.map((c) => [c.value, c.sedes])
);

const IconGrid = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
    <rect x="0" y="0" width="6" height="6" rx="1" />
    <rect x="8" y="0" width="6" height="6" rx="1" />
    <rect x="0" y="8" width="6" height="6" rx="1" />
    <rect x="8" y="8" width="6" height="6" rx="1" />
  </svg>
);

const IconTable = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
    <rect x="0" y="0" width="14" height="2" />
    <rect x="0" y="5" width="14" height="2" />
    <rect x="0" y="10" width="14" height="2" />
  </svg>
);

const normalizarFechaSoloDia = (valor) => {
  if (!valor) return null;

  const str = String(valor).trim().slice(0, 10);
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const [, y, m, d] = match;
  return new Date(Number(y), Number(m) - 1, Number(d));
};

const novedadVigente = (novedadInicio, novedadFin) => {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  if (novedadInicio) {
    const inicio = normalizarFechaSoloDia(novedadInicio);
    if (!inicio || inicio > hoy) return false;
  }

  if (novedadFin) {
    const fin = normalizarFechaSoloDia(novedadFin);
    if (!fin || fin < hoy) return false;
  }

  // Sin fechas se conserva la semántica histórica: visible mientras haya texto.
  return true;
};

const limpiarNovedadExpirada = (row) => {
  const texto = row?.novedad ? String(row.novedad).trim() : '';
  if (!texto) {
    return {
      ...row,
      novedad: '',
      novedad_inicio: row?.novedad_inicio || null,
      novedad_fin: row?.novedad_fin || null,
    };
  }

  if (!novedadVigente(row?.novedad_inicio, row?.novedad_fin)) {
    return {
      ...row,
      novedad: '',
      novedad_inicio: null,
      novedad_fin: null,
    };
  }

  return row;
};

const normalizarBusqueda = (valor) =>
  String(valor || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const HorariosConsulta = () => {
  const [sede, setSede] = useState('1');
  const [carrera, setCarrera] = useState('003');
  const [plan, setPlan] = useState('4');
  const [idMat, setIdMat] = useState('');
  const [matNombre, setMatNombre] = useState('');

  const [materias, setMaterias] = useState([]);
  const [docentes, setDocentes] = useState([]);
  const [vista, setVista] = useState('table');
  const [loadingMat, setLoadingMat] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [loadingProfesor, setLoadingProfesor] = useState(false);
  const [buscandoDocentes, setBuscandoDocentes] = useState(false);
  const [error, setError] = useState('');

  const [modoConsulta, setModoConsulta] = useState('materia');

  const [actividadBusqueda, setActividadBusqueda] = useState('');
  const [mostrarSugerenciasMaterias, setMostrarSugerenciasMaterias] = useState(false);

  const [profesorBusqueda, setProfesorBusqueda] = useState('');
  const [sugerenciasDocentes, setSugerenciasDocentes] = useState([]);
  const [docenteSeleccionado, setDocenteSeleccionado] = useState(null);
  const [mostrarSugerencias, setMostrarSugerencias] = useState(false);

  const docenteRequestId = useRef(0);

  const esLogistica = carrera === '007';

  const sedesValidasCarrera = useMemo(() => {
    return SEDES_POR_CARRERA[carrera] || SEDES.map((s) => s.value);
  }, [carrera]);

  const sedesDisponibles = useMemo(() => {
    return SEDES.filter((s) => sedesValidasCarrera.includes(s.value));
  }, [sedesValidasCarrera]);

  const planesDisponibles = useMemo(() => {
    return esLogistica ? PLANES_LOGISTICA : PLANES_DEFAULT;
  }, [esLogistica]);

  const obtenerPlanesAConsultar = useCallback(() => [plan], [plan]);

  const obtenerEtiquetaSede = useCallback((valorSede) => {
    return SEDES.find((s) => s.value === String(valorSede))?.label || `Sede ${valorSede}`;
  }, []);

  const obtenerEtiquetaCarrera = useCallback((valorCarrera) => {
    return CARRERAS_CONFIG.find((c) => c.value === String(valorCarrera))?.label || `Carrera ${valorCarrera}`;
  }, []);

  const obtenerEtiquetaPlan = useCallback((valorPlan) => {
    const catalogo = esLogistica ? PLANES_LOGISTICA : PLANES_DEFAULT;
    return catalogo.find((p) => p.value === String(valorPlan))?.label || `Plan ${valorPlan}`;
  }, [esLogistica]);

  const formatearActividad = useCallback((materia, idActividad) => {
    if (!materia && !idActividad) return '';
    if (!materia) return `${idActividad}`;
    if (!idActividad) return materia;
    return `${materia} (${idActividad})`;
  }, []);

  const normalizarMateria = (m) => ({
    ...m,
    // /consultamv devuelve id_mat y /materias devuelve id_materia.
    // Se normalizan ambos formatos para que el buscador use siempre id_mat.
    id_mat:
      m?.id_mat != null
        ? String(m.id_mat).trim()
        : m?.id_materia != null
          ? String(m.id_materia).trim()
          : '',
    materia: m?.materia ? String(m.materia).trim() : '',
  });

  const deduplicarMaterias = useCallback((lista) => {
    const map = new Map();

    lista.forEach((item) => {
      const m = normalizarMateria(item);
      const key = `${m.id_mat}__${m.materia}`;
      if (!map.has(key)) {
        map.set(key, m);
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      (a.materia || '').localeCompare(b.materia || '', 'es', { sensitivity: 'base' })
    );
  }, []);

  const deduplicarDocentes = useCallback((lista) => {
    const map = new Map();

    lista.forEach((item, index) => {
      const limpio = limpiarNovedadExpirada(item);

      const key = limpio.id_hora
        ? `id_hora_${limpio.id_hora}`
        : [
            limpio.leg ?? '',
            limpio.apellido ?? '',
            limpio.id_mat ?? '',
            limpio.lunes ?? '',
            limpio.martes ?? '',
            limpio.miercoles ?? '',
            limpio.jueves ?? '',
            limpio.viernes ?? '',
            limpio.lugar_c ?? '',
            limpio.lugar_v ?? '',
            limpio.novedad ?? '',
          ].join('__');

      if (!map.has(key)) {
        map.set(key, { ...limpio, __idx: index });
      }
    });

    return Array.from(map.values()).map(({ __idx, ...rest }) => rest);
  }, []);

  const filtrarHorariosDocente = useCallback((rows) => {
    const sedeSeleccionada = String(sede ?? '');
    const carreraSeleccionada = String(carrera ?? '');

    return rows.filter((row) => {
      const rowSede = String(row.sede ?? '');
      const rowCarrera = String(row.carrera ?? '');
      const rowPlan = String(row.plan ?? '');

      return (
        rowSede === sedeSeleccionada &&
        rowCarrera === carreraSeleccionada &&
        rowPlan === String(plan ?? '')
      );
    });
  }, [sede, carrera, plan]);

  const sugerenciasMaterias = useMemo(() => {
    if (modoConsulta !== 'materia' || !mostrarSugerenciasMaterias) return [];

    const patron = normalizarBusqueda(actividadBusqueda);
    const filtradas = patron
      ? materias.filter((m) => {
          const nombre = normalizarBusqueda(m.materia);
          const codigo = normalizarBusqueda(m.id_mat);
          return nombre.includes(patron) || codigo.includes(patron);
        })
      : materias;

    // Se muestra la lista completa del plan; el contenedor tiene scroll.
    return filtradas;
  }, [actividadBusqueda, materias, modoConsulta, mostrarSugerenciasMaterias]);

  const limpiarResultados = useCallback(() => {
    setDocentes([]);
    setMatNombre('');
    setError('');
  }, []);

  const limpiarSeleccionActividad = useCallback(() => {
    setIdMat('');
    setActividadBusqueda('');
    setMostrarSugerenciasMaterias(false);
  }, []);

  const limpiarSeleccionProfesor = useCallback(() => {
    docenteRequestId.current += 1;
    setProfesorBusqueda('');
    setSugerenciasDocentes([]);
    setMostrarSugerencias(false);
    setDocenteSeleccionado(null);
    setBuscandoDocentes(false);
  }, []);

  useEffect(() => {
    if (!sedesValidasCarrera.includes(sede)) {
      setSede(sedesValidasCarrera[0] || '1');
    }
  }, [carrera, sede, sedesValidasCarrera]);

  useEffect(() => {
    if (esLogistica) {
      if (plan !== '1' && plan !== '2') {
        setPlan('1');
      }
    } else if (plan === '1' || plan === '2') {
      setPlan('4');
    }
  }, [esLogistica, plan]);

  const cargarMaterias = useCallback(async () => {
    setLoadingMat(true);
    limpiarResultados();
    limpiarSeleccionActividad();
    limpiarSeleccionProfesor();

    const planesAConsultar = obtenerPlanesAConsultar();

    try {
      const responses = await Promise.all(
        planesAConsultar.map((p) =>
          // Solo se listan actividades que poseen al menos un horario vigente
          // cargado para la sede, carrera y plan seleccionados.
          axios.get(`${API_BASE_URL}consultamv/${sede}/${carrera}/${p}`)
        )
      );

      const dataUnificada = responses.flatMap((res) =>
        Array.isArray(res.data) ? res.data : []
      );

      setMaterias(deduplicarMaterias(dataUnificada));
    } catch (e) {
      console.error('Error cargando materias:', e);
      setMaterias([]);
      setError('No se pudo conectar al servidor. Verificá tu conexión.');
    } finally {
      setLoadingMat(false);
    }
  }, [sede, carrera, obtenerPlanesAConsultar, deduplicarMaterias, limpiarResultados, limpiarSeleccionActividad, limpiarSeleccionProfesor]);

  useEffect(() => {
    cargarMaterias();
  }, [cargarMaterias]);

  useEffect(() => {
    if (modoConsulta !== 'materia' || !idMat) return undefined;

    let activo = true;

    const cargarDocentes = async () => {
      setLoadingDoc(true);
      setError('');
      setDocentes([]);

      const planesAConsultar = obtenerPlanesAConsultar();

      try {
        const responses = await Promise.all(
          planesAConsultar.map((p) =>
            axios.get(`${API_BASE_URL}consultam/${sede}/${carrera}/${p}/${idMat}`)
          )
        );

        if (!activo) return;

        const rows = responses.flatMap((res) => (Array.isArray(res.data) ? res.data : []));
        const materiaSeleccionada = materias.find((m) => String(m.id_mat) === String(idMat));

        const normalizados = rows.map((row) =>
          limpiarNovedadExpirada({
            ...row,
            materia: row.materia || materiaSeleccionada?.materia || '',
            id_mat: row.id_mat || materiaSeleccionada?.id_mat || idMat,
          })
        );

        setDocentes(deduplicarDocentes(normalizados));

        if (materiaSeleccionada) {
          setMatNombre(
            formatearActividad(materiaSeleccionada.materia, materiaSeleccionada.id_mat)
          );
        }
      } catch (e) {
        if (!activo) return;
        console.error('Error cargando docentes:', e);
        setError('No se pudieron obtener los horarios para esta actividad.');
      } finally {
        if (activo) setLoadingDoc(false);
      }
    };

    cargarDocentes();

    return () => {
      activo = false;
    };
  }, [idMat, sede, carrera, plan, modoConsulta, materias, obtenerPlanesAConsultar, deduplicarDocentes, formatearActividad]);

  useEffect(() => {
    const patron = profesorBusqueda.trim();

    if (modoConsulta !== 'docente' || patron.length < 2 || !mostrarSugerencias) {
      setSugerenciasDocentes([]);
      setBuscandoDocentes(false);
      return undefined;
    }

    const requestId = ++docenteRequestId.current;
    setBuscandoDocentes(true);

    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}docentes/${encodeURIComponent(patron)}`);
        if (requestId !== docenteRequestId.current) return;

        setSugerenciasDocentes(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (requestId !== docenteRequestId.current) return;
        console.error('Error buscando docentes:', e);
        setSugerenciasDocentes([]);
      } finally {
        if (requestId === docenteRequestId.current) {
          setBuscandoDocentes(false);
        }
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [profesorBusqueda, mostrarSugerencias, modoConsulta]);

  const buscarHorarioDocente = async (docente) => {
    if (!docente?.legajo) return;

    docenteRequestId.current += 1;
    setMostrarSugerencias(false);
    setSugerenciasDocentes([]);
    setDocenteSeleccionado(docente);
    setProfesorBusqueda(docente.apellido || String(docente.legajo));
    setLoadingProfesor(true);
    setLoadingDoc(false);
    setError('');
    setDocentes([]);

    try {
      const res = await axios.get(`${API_BASE_URL}hconsudoc/${docente.legajo}`);
      const rows = Array.isArray(res.data) ? res.data : [];
      const filtrados = filtrarHorariosDocente(rows);

      const normalizados = filtrados.map((row) =>
        limpiarNovedadExpirada({
          ...row,
          apellido: docente.apellido,
        })
      );

      setDocentes(deduplicarDocentes(normalizados));
      setMatNombre(`Horarios de ${docente.apellido}`);
    } catch (e) {
      console.error('Error cargando horarios del docente:', e);
      setError('No se pudieron obtener los horarios del docente seleccionado.');
    } finally {
      setLoadingProfesor(false);
    }
  };

  const seleccionarModo = (nuevoModo) => {
    if (nuevoModo === modoConsulta) return;

    setModoConsulta(nuevoModo);
    limpiarResultados();
    limpiarSeleccionActividad();
    limpiarSeleccionProfesor();
  };

  const handleSedeChange = (e) => setSede(e.target.value);

  const handleCarreraChange = (e) => {
    const nuevaCarrera = e.target.value;
    const sedesValidasNuevaCarrera = SEDES_POR_CARRERA[nuevaCarrera] || SEDES.map((s) => s.value);

    setCarrera(nuevaCarrera);

    if (!sedesValidasNuevaCarrera.includes(sede)) {
      setSede(sedesValidasNuevaCarrera[0] || '1');
    }

    if (nuevaCarrera === '007') {
      setPlan('1');
    } else if (plan === '1' || plan === '2') {
      setPlan('4');
    }
  };

  const handlePlanChange = (e) => setPlan(e.target.value);

  const seleccionarActividad = (materia) => {
    if (!materia?.id_mat) return;

    setIdMat(String(materia.id_mat));
    setActividadBusqueda(formatearActividad(materia.materia, materia.id_mat));
    setMostrarSugerenciasMaterias(false);
    setDocenteSeleccionado(null);
    setDocentes([]);
    setMatNombre(formatearActividad(materia.materia, materia.id_mat));
  };

  const handleActividadInputChange = (e) => {
    const value = e.target.value;
    setActividadBusqueda(value);
    setMostrarSugerenciasMaterias(true);

    // Si el usuario modifica el texto después de seleccionar una materia,
    // la selección deja de ser válida hasta elegir una sugerencia nuevamente.
    if (idMat) {
      setIdMat('');
      limpiarResultados();
    }
  };

  const handleActividadKeyDown = (e) => {
    if (e.key === 'Escape') {
      setMostrarSugerenciasMaterias(false);
      return;
    }

    if (e.key === 'Enter' && sugerenciasMaterias.length > 0) {
      e.preventDefault();
      seleccionarActividad(sugerenciasMaterias[0]);
    }
  };

  const handleProfesorInputChange = (e) => {
    const value = e.target.value;
    setProfesorBusqueda(value);
    setMostrarSugerencias(true);

    if (docenteSeleccionado) {
      setDocenteSeleccionado(null);
      limpiarResultados();
    }

    if (!value.trim()) {
      setSugerenciasDocentes([]);
      setMostrarSugerencias(false);
    }
  };

  const handleProfesorKeyDown = (e) => {
    if (e.key === 'Escape') {
      setMostrarSugerencias(false);
      return;
    }

    if (e.key === 'Enter' && sugerenciasDocentes.length > 0) {
      e.preventDefault();
      buscarHorarioDocente(sugerenciasDocentes[0]);
    }
  };

  const limpiarBusquedaProfesor = () => {
    limpiarSeleccionProfesor();
    limpiarResultados();
  };

  const limpiarBusquedaActividad = () => {
    limpiarSeleccionActividad();
    limpiarResultados();
  };

  const renderContenido = () => {
    if (loadingMat || loadingDoc || loadingProfesor) {
      return (
        <div className="hcd-loading">
          <div className="hcd-spinner" />
          Cargando horarios…
        </div>
      );
    }

    if (error) {
      return (
        <div className="hcd-empty">
          <div className="hcd-empty-icon">⚠</div>
          <div className="hcd-empty-title">Error de conexión</div>
          <div className="hcd-empty-desc">{error}</div>
        </div>
      );
    }

    if (modoConsulta === 'docente' && !docenteSeleccionado) {
      return (
        <div className="hcd-empty">
          <div className="hcd-empty-icon">👤</div>
          <div className="hcd-empty-title">Buscá un profesor</div>
          <div className="hcd-empty-desc">
            Escribí al menos dos caracteres del apellido y seleccioná un profesor.
          </div>
        </div>
      );
    }

    if (modoConsulta === 'docente' && docenteSeleccionado && docentes.length === 0) {
      return (
        <div className="hcd-empty">
          <div className="hcd-empty-icon">👤</div>
          <div className="hcd-empty-title">Sin horarios para este docente</div>
          <div className="hcd-empty-desc">
            {docenteSeleccionado.apellido} no tiene horarios de consulta para{' '}
            <strong>{obtenerEtiquetaSede(sede)}</strong>,{' '}
            <strong>{obtenerEtiquetaCarrera(carrera)}</strong> y{' '}
            <strong>{obtenerEtiquetaPlan(plan)}</strong>.
          </div>
        </div>
      );
    }

    if (modoConsulta === 'materia' && (!idMat || materias.length === 0)) {
      return (
        <div className="hcd-empty">
          <div className="hcd-empty-icon">📋</div>
          <div className="hcd-empty-title">
            {materias.length === 0 ? 'Sin actividades disponibles' : 'Buscá una actividad'}
          </div>
          <div className="hcd-empty-desc">
            {materias.length === 0
              ? 'No hay materias cargadas para la carrera y plan seleccionados.'
              : 'Elegí una materia de la lista o buscala por nombre o código para ver sus horarios.'}
          </div>
        </div>
      );
    }

    if (modoConsulta === 'materia' && docentes.length === 0) {
      return (
        <div className="hcd-empty">
          <div className="hcd-empty-icon">🗓</div>
          <div className="hcd-empty-title">Sin horarios cargados</div>
          <div className="hcd-empty-desc">
            No hay docentes con horario de consulta informado para esta actividad.
          </div>
        </div>
      );
    }

    return vista === 'table'
      ? <ConsuTabla matdocentes={docentes} mat={matNombre} />
      : <ConsuCards matdocentes={docentes} mat={matNombre} />;
  };

  return (
    <div className="hcd-wrapper">
      <header className="hcd-header">
        <div className="hcd-logo-block">
          <div className="hcd-logo-badge">
            <img
              src="/economicas.png"
              alt="FCE Logo"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </div>

          <div>
            <div className="hcd-logo-text">Horarios de Consulta</div>
            <div className="hcd-logo-sub">Facultad de Ciencias Económicas · UNCuyo</div>
          </div>

          <Link to="/" className="hcd-home-link">
            Inicio
          </Link>
        </div>

        <div className="hcd-view-toggle">
          <button
            className={`hcd-view-btn${vista === 'cards' ? ' active' : ''}`}
            type="button"
            onClick={() => setVista('cards')}
          >
            <IconGrid /> Tarjetas
          </button>
          <button
            className={`hcd-view-btn${vista === 'table' ? ' active' : ''}`}
            type="button"
            onClick={() => setVista('table')}
          >
            <IconTable /> Tabla
          </button>
        </div>
      </header>

      <div className="hcd-filters">
        <div className="hcd-filter-group">
          <label className="hcd-filter-label" htmlFor="hcd-carrera">Carrera</label>
          <select
            id="hcd-carrera"
            className="hcd-select"
            value={carrera}
            onChange={handleCarreraChange}
          >
            {CARRERAS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div className="hcd-filter-group">
          <label className="hcd-filter-label" htmlFor="hcd-plan">Plan</label>
          <select id="hcd-plan" className="hcd-select" value={plan} onChange={handlePlanChange}>
            {planesDisponibles.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <div className="hcd-filter-group">
          <label className="hcd-filter-label" htmlFor="hcd-sede">Sede</label>
          <select id="hcd-sede" className="hcd-select" value={sede} onChange={handleSedeChange}>
            {sedesDisponibles.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="hcd-search-panel">
        <div className="hcd-search-mode-row">
          <span className="hcd-search-mode-label">Buscar horarios por</span>
          <div className="hcd-mode-toggle" role="group" aria-label="Tipo de búsqueda">
            <button
              type="button"
              className={`hcd-mode-btn${modoConsulta === 'materia' ? ' active' : ''}`}
              onClick={() => seleccionarModo('materia')}
            >
              Actividad
            </button>
            <button
              type="button"
              className={`hcd-mode-btn${modoConsulta === 'docente' ? ' active' : ''}`}
              onClick={() => seleccionarModo('docente')}
            >
              Profesor
            </button>
          </div>
        </div>

        {modoConsulta === 'materia' ? (
          <div className="hcd-search-field">
            <label className="hcd-filter-label" htmlFor="hcd-actividad">Actividad</label>
            <div className="hcd-search-wrap">
              <input
                id="hcd-actividad"
                className="hcd-input"
                type="text"
                value={actividadBusqueda}
                onChange={handleActividadInputChange}
                onKeyDown={handleActividadKeyDown}
                onFocus={() => setMostrarSugerenciasMaterias(true)}
                placeholder={loadingMat ? 'Cargando actividades…' : 'Nombre o código de actividad...'}
                autoComplete="off"
                disabled={loadingMat || materias.length === 0}
              />

              {actividadBusqueda && (
                <button
                  type="button"
                  className="hcd-clear-btn"
                  onClick={limpiarBusquedaActividad}
                  aria-label="Limpiar búsqueda de actividad"
                >
                  ✕
                </button>
              )}

              {mostrarSugerenciasMaterias && sugerenciasMaterias.length > 0 && (
                <div className="hcd-search-results">
                  {sugerenciasMaterias.map((m) => (
                    <button
                      key={`${m.id_mat}-${m.materia}`}
                      type="button"
                      className="hcd-search-item"
                      onClick={() => seleccionarActividad(m)}
                    >
                      <span className="hcd-search-name">{m.materia}</span>
                      <span className="hcd-search-meta">{m.id_mat}</span>
                    </button>
                  ))}
                </div>
              )}

              {mostrarSugerenciasMaterias && actividadBusqueda.trim() && sugerenciasMaterias.length === 0 && (
                <div className="hcd-search-results hcd-search-status">
                  No se encontraron actividades con ese nombre o código.
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="hcd-search-field">
            <label className="hcd-filter-label" htmlFor="hcd-profesor">Profesor</label>
            <div className="hcd-search-wrap">
              <input
                id="hcd-profesor"
                className="hcd-input"
                type="text"
                value={profesorBusqueda}
                onChange={handleProfesorInputChange}
                onKeyDown={handleProfesorKeyDown}
                onFocus={() => {
                  if (profesorBusqueda.trim().length >= 2) setMostrarSugerencias(true);
                }}
                placeholder="Apellido del profesor..."
                autoComplete="off"
              />

              {profesorBusqueda && (
                <button
                  type="button"
                  className="hcd-clear-btn"
                  onClick={limpiarBusquedaProfesor}
                  aria-label="Limpiar búsqueda de profesor"
                >
                  ✕
                </button>
              )}

              {mostrarSugerencias && sugerenciasDocentes.length > 0 && (
                <div className="hcd-search-results">
                  {sugerenciasDocentes.map((doc) => (
                    <button
                      key={doc.legajo}
                      type="button"
                      className="hcd-search-item"
                      onClick={() => buscarHorarioDocente(doc)}
                    >
                      <span className="hcd-search-name">{doc.apellido}</span>
                    </button>
                  ))}
                </div>
              )}

              {mostrarSugerencias && profesorBusqueda.trim().length >= 2 && buscandoDocentes && (
                <div className="hcd-search-results hcd-search-status">Buscando docentes…</div>
              )}

              {mostrarSugerencias && profesorBusqueda.trim().length >= 2 && !buscandoDocentes && sugerenciasDocentes.length === 0 && (
                <div className="hcd-search-results hcd-search-status">
                  No se encontraron profesores con ese apellido.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <main className="hcd-content">
        {renderContenido()}
      </main>
    </div>
  );
};

export default HorariosConsulta;
